import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * The single source of truth for "is this a phone-sized viewport".
 *
 * WHY A HOOK AND NOT CSS: Tailwind breakpoints stay the tool for *styling* one
 * element (size, padding, columns, hiding leaf decoration). This hook is for
 * *decisions* — when the alternative is rendering two subtrees and hiding one
 * with `hidden md:*`, which duplicates DOM, handlers and (fatally) `id`
 * attributes that onboarding tours resolve with `getElementById`.
 *
 * Rule of thumb, mirrored in Standards.md:
 *   choosing between two different things  -> this hook
 *   adjusting how one thing looks          -> Tailwind `md:` classes
 *
 * One `matchMedia` listener lives in the provider and every consumer reads it
 * from context, so N components cost one subscription. Same shape as
 * `ThemeContext`, which is the in-repo precedent for a media-query context.
 */

/**
 * Mobile is everything below Tailwind's `md` (768px), so the query caps at
 * 767px. Keep this in lockstep with the `md:` prefix — a component that asks
 * the hook and a sibling that uses `md:` must flip at the same pixel.
 */
export const MOBILE_MAX_WIDTH = 767;

const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

/**
 * Read the current match synchronously. Used as the `useState` initialiser so
 * the very first paint is already correct — both apps are client-only Vite
 * SPAs, so there is no server render to mismatch against.
 */
const readIsMobile = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
};

const ViewportContext = createContext<boolean | undefined>(undefined);

export const ViewportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isMobile, setIsMobile] = useState<boolean>(readIsMobile);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia(MOBILE_QUERY);
        const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener('change', onChange);
        // The viewport can change between the initial render and this effect
        // (rotation during boot, devtools opening) — resync once.
        setIsMobile(mql.matches);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    return <ViewportContext.Provider value={isMobile}>{children}</ViewportContext.Provider>;
};

/**
 * `true` below 768px. Only re-renders when the breakpoint is *crossed*, not on
 * every resize pixel.
 *
 * Throws without a provider, like `useTheme` — both apps mount
 * `ViewportProvider` at the root of `App.tsx`.
 */
export const useIsMobile = (): boolean => {
    const ctx = useContext(ViewportContext);
    if (ctx === undefined) {
        throw new Error('useIsMobile must be used within a ViewportProvider');
    }
    return ctx;
};
